/* Sohi — scroll reveal. Fades a group's children up as the group enters the
   viewport, staggered a few frames apart.

   Opt-in from markup: put data-reveal on a container and its direct children
   get .is-revealed (and a --reveal-i stagger index) once the container
   scrolls into view. See sohi.css for the actual animation — this file only
   ever adds/removes a class and a custom property.

   Nothing is hidden by CSS alone: sohi.css only hides [data-reveal] children
   under .sohi-js, and THIS FILE is the only thing that ever adds .sohi-js to
   <html> — deliberately not an inline bootstrap in each page's <head>. If an
   inline snippet added the class instead, a blocked or 404'd sohi-reveal.js
   would leave .sohi-js in place with nothing left to ever add .is-revealed —
   permanently hiding every product. Gating the class on this file's own
   successful execution means the failure modes (JS off, script blocked,
   reduced motion) all leave every item visible; the cost is a possible
   single frame of unhidden content before this runs, which is preferable. */
(function () {
  "use strict";

  document.documentElement.classList.add("sohi-js");

  var REDUCED = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var SUPPORTED = "IntersectionObserver" in window;

  // Past ~6 items the stagger reads as a queue rather than a cascade, so the
  // index is capped instead of the delay — item 40 waits as long as item 6.
  var MAX_STEP = 5;

  var observer = null;

  function reveal(group, instant) {
    var items = group.children;
    for (var i = 0; i < items.length; i++) {
      items[i].style.setProperty("--reveal-i", instant ? "0" : String(Math.min(i, MAX_STEP)));
      items[i].classList.add("is-revealed");
    }
  }

  function getObserver() {
    if (observer) return observer;
    // threshold:0.01 + a positive bottom rootMargin (grows the trigger zone
    // downward, rather than the -10% that used to shrink it) — a tall group
    // just barely poking into a short viewport (a phone with an address bar
    // eating a chunk of it) needs to fire on that first sliver, not wait for
    // 12% of its own height to clear a zone that's already been shrunk.
    observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        reveal(entry.target, false);
      });
    }, { threshold: 0.01, rootMargin: "0px 0px 15% 0px" });
    return observer;
  }

  /* Show a group's items with no animation. Used for content that appears in
     response to something the visitor just did (a filter re-render), where
     replaying the intro would read as the page reloading rather than as
     polish. */
  function show(group) {
    if (!group) return;
    reveal(group, true);
  }

  /* Arm a group. Called on load, on shopify:section:load, and by
     sohi-shop.js after it rewrites a grid. A group already armed once is not
     re-armed — it is shown instantly instead.

     A group with no children yet (e.g. sohi-shop.js's grids are empty
     <ul>s at parse time, filled in later on the same DOMContentLoaded
     tick) is skipped rather than armed: an empty element is zero-height
     and reads as "intersecting" immediately, which would mark it done
     before it has anything worth revealing — so the later observe() call
     once it's populated would find revealDone already set and skip the
     animation entirely. Whoever populates the group is responsible for
     calling observe() again once it has content. */
  function observe(group) {
    if (!group) return;
    if (!group.children.length) return;
    if (REDUCED || !SUPPORTED) { show(group); return; }
    if (group.dataset.revealDone === "true") { show(group); return; }
    group.dataset.revealDone = "true";
    getObserver().observe(group);
  }

  function boot(root) {
    root = root || document;
    if (root.nodeType === 1 && root.matches && root.matches("[data-reveal]")) observe(root);
    root.querySelectorAll("[data-reveal]").forEach(observe);
  }

  window.SohiReveal = { observe: observe, show: show, boot: boot };

  // A caller (sohi-shop.js re-rendering a grid) can run before this script
  // has finished loading if the two are ever reordered or this file is
  // slow to fetch — `if (window.SohiReveal) …` at the call site would then
  // silently drop that grid, leaving it at opacity:0 forever with nothing
  // left to ever reveal it. Anything queued here while this file wasn't
  // ready yet is queued by sohi-shop.js's own SohiRevealQueue fallback (see
  // that file) and flushed the moment this module exists.
  if (window.SohiRevealQueue && window.SohiRevealQueue.length) {
    window.SohiRevealQueue.splice(0).forEach(function (group) { observe(group); });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () { boot(); });
  } else {
    boot();
  }

  document.addEventListener("shopify:section:load", function (e) { boot(e.target); });
})();
